import { lazy, Suspense, useMemo, useContext } from "react"
import { Loader } from "widgets"
import RouteError from "../base/route-error.js"
import PrivateRoute from "../base/private-route.js"
import { featuresFlatMap } from "utils"
import { AppContext } from "../base/app-context-provider.js"
import VisaeroAgentModule from "../visaero/agent-module.js"

const ManageVisaTransactions = lazy(
  () => import("./manage-visa-transactions.js"),
)

const VisaTransactionDetails = lazy(
  () => import("./visa-transaction-details.js"),
)

const VisaAncillaryDetails = lazy(
  () => import("./visa-ancillary-details.js"),
)

const CancelVisaTransaction = lazy(() => import("./cancel-visa-transaction.js"))
const VisaeroLogin = lazy(() => import("../visaero/login.js"))

export const useVisaRoutes = () => {
  const appContext = useContext(AppContext)
  const features = useMemo(
    () =>
      _.merge(
        {},
        featuresFlatMap(appContext?.user?.featureDTOList)?.["e-visa"],
        featuresFlatMap(appContext?.user?.featureDTOList)?.["home"],
        featuresFlatMap(appContext?.user?.featureDTOList)?.["help desk"],
      ) ?? [],
    [appContext?.user?.featureDTOList],
  )

  const visaRoutes = useMemo(
    () =>
      _.compact([
        {
          path: "visa",
          errorElement: <RouteError />,
          element: <PrivateRoute />,
          children: _.compact([
            features["issue-visa"] && {
              path: "issue-visa",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaeroAgentModule type="issue" />
                </Suspense>
              ),
            },
            features["track-visa"] && {
              path: "track-visa",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaeroAgentModule type="track" />
                </Suspense>
              ),
            },
            features["visa-service"] && {
              path: "visa-service",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaeroLogin />
                </Suspense>
              ),
            },
            features["visa"] && {
              path: "visa-service",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaeroLogin />
                </Suspense>
              ),
            },
            features["search-visa-transaction"] && {
              path: "manage-visa-transactions",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageVisaTransactions
                    title={t("visa.manage_visa_txn")}
                    heading={t("visa.search_visa_txn")}
                  />
                </Suspense>
              ),
            },
            features["search-my-visa-transactions"] && {
              path: "manage-my-visa-transactions",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageVisaTransactions
                    title={t("visa.my_visa_txn")}
                    heading={t("visa.search_visa_txn")}
                    type="self"
                  />
                </Suspense>
              ),
            },
            features["add-visa-transaction"] && {
              path: "add-visa-transaction",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaTransactionDetails
                    mode="add"
                    title={t("visa.manage_visa_transaction")}
                    formHeading={t("visa.add_visa_transaction")}
                    parentRoute="/visa/manage-visa-transactions"
                  />
                </Suspense>
              ),
            },
            features["add-visa-ancillary"] &&
             {
              path: "add-visa-ancillary",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaAncillaryDetails
                    mode="add"
                    title={t("visa.manage_visa_transaction")}
                    formHeading={t("visa.add_visa_ancillary")}
                    parentRoute="/visa/manage-visa-transactions"
                  />
                </Suspense>
              ),
            },
            features["view-visa-ancillary"] &&
             {
              path: "view-visa-ancillary",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaAncillaryDetails
                    mode="view"
                    title={t("visa.manage_visa_transaction")}
                    formHeading={t("visa.view_visa_ancillary")}
                    parentRoute="/visa/manage-visa-transactions"
                  />
                </Suspense>
              ),
            },
            features["view-visa-transaction"] && {
              path: "view-visa-transaction",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaTransactionDetails
                    mode="view"
                    title={t("visa.manage_visa_transaction")}
                    formHeading={t("visa.view_transaction")}
                    parentRoute="/visa/manage-visa-transactions"
                  />
                </Suspense>
              ),
            },
            features["view-visa-transaction"] && {
              path: "view-my-visa-transaction",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaTransactionDetails
                    mode="view"
                    title={t("visa.manage_visa_transaction")}
                    formHeading={t("visa.view_transaction")}
                    parentRoute="/visa/manage-my-visa-transactions"
                  />
                </Suspense>
              ),
            },
            features["cancel-visa-transactions"] && {
              path: "cancel-my-visa-transaction",
              element: (
                <Suspense fallback={<Loader />}>
                  <CancelVisaTransaction
                    parentRoute="/visa/manage-my-visa-transactions"
                    mode="create"
                  />
                </Suspense>
              ),
            },
            features["search-agency-requests"] && {
              path: "cancel-visa-transaction",
              element: (
                <Suspense fallback={<Loader />}>
                  <CancelVisaTransaction
                    parentRoute="/help-desk/manage-agency-requests"
                    mode="update"
                  />
                </Suspense>
              ),
            },
            features["search-agency-requests"] && {
              path: "view-cancel-transaction-request",
              element: (
                <Suspense fallback={<Loader />}>
                  <CancelVisaTransaction
                    parentRoute="/help-desk/manage-agency-requests"
                    mode="view-processed-request"
                  />
                </Suspense>
              ),
            },
            features["search-my-visa-transactions"] && {
              path: "view-my-cancel-transaction-request",
              element: (
                <Suspense fallback={<Loader />}>
                  <CancelVisaTransaction
                    parentRoute="/help-desk/my-requests"
                    mode="view-processed-request"
                  />
                </Suspense>
              ),
            },
            features["modify-visa-transaction"] && {
              path: "modify-visa-transaction",
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaTransactionDetails
                    mode="modify"
                    title={t("visa.manage_visa_transaction")}
                    formHeading={t("visa.modify_transaction")}
                    parentRoute="/visa/manage-visa-transactions"
                  />
                </Suspense>
              ),
            },
          ]),
        },
      ]),
    [features],
  )

  return visaRoutes
}
